import { motion } from "framer-motion";
import { Target, Network, TrendingUp, LucideIcon } from "lucide-react";

interface Frente {
  icon: LucideIcon;
  titulo: string;
  subtitulo: string;
  descripcion: string;
  gradient: string;
}

const frentes: Frente[] = [
  {
    icon: Target,
    titulo: "La pyme",
    subtitulo: "Juego directo",
    descripcion:
      "Le hablás de sus problemas reales, no de indicadores. Conectás, no comunicás. El usuario te elige y el que conquista al usuario conquista el mercado.",
    gradient: "linear-gradient(135deg, #1d4a8c, #3B8BD4)",
  },
  {
    icon: Network,
    titulo: "Los fondos",
    subtitulo: "Juego indirecto",
    descripcion:
      "No les hablás a ellos: te ven jugando para tu público, poniendo el cuerpo.\nTe ven elegido por el mercado que a ellos les interesa. Ese es el puente a la escala internacional.",
    gradient: "linear-gradient(135deg, #3B8BD4, #5BA3E0)",
  },
  {
    icon: TrendingUp,
    titulo: "Tu proyección",
    subtitulo: "Latam 2027",
    descripcion:
      "La autoridad de categoría no está atada a una sola plataforma ni a un solo país. Es un activo que te acompaña a donde vayas, no un cargo del que quedás dependiendo.",
    gradient: "linear-gradient(135deg, #0F6E56, #1D9E75)",
  },
];

const GastonFrentes = () => {
  return (
    <section
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: "#1d4a8c" }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(91,163,224,0.20), transparent 60%)",
        }}
        aria-hidden="true"
      />

      <div className="container px-4 max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold tracking-[0.25em] uppercase mb-4 block text-white/80">
            Una construcción, tres frentes
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1]">
            Tu autoridad juega
            <br />
            <span style={{ color: "#9CCBF0" }}>en tres tableros a la vez.</span>
          </h2>
        </motion.div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-5 mb-14">
          {frentes.map((f, index) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.titulo}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: index * 0.12 }}
                className="rounded-2xl p-7 border border-white/15 backdrop-blur-sm"
                style={{ background: "rgba(10,22,42,0.55)" }}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: f.gradient }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-semibold text-white leading-snug">
                  {f.titulo}
                </h3>
                <p
                  className="text-sm font-semibold uppercase tracking-[0.15em] mt-1 mb-4"
                  style={{ color: index === 2 ? "#4FD1A5" : "#9CCBF0" }}
                >
                  {f.subtitulo}
                </p>
                <p className="text-sm md:text-base text-white/75 leading-relaxed whitespace-pre-line">
                  {f.descripcion}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Text block */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-base md:text-lg text-white/80 leading-relaxed max-w-4xl mx-auto text-center mb-10"
        >
          Mercado Cheque es el vehículo natural de todo esto, es donde buscás
          ser visible, y con razón, porque es la plataforma que lo necesita.
          GWINN es distinto: es uno a uno, y no te precisa en el escenario.
          Definir con precisión cómo se relacionan tu figura, Mercado Cheque y
          GWINN es una de las primeras cosas que resolvemos.
        </motion.p>

        {/* Closing card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="rounded-2xl p-8 md:p-10 max-w-4xl mx-auto border-l-4"
          style={{
            borderColor: "#1D9E75",
            background: "rgba(10,22,42,0.65)",
          }}
        >
          <p className="font-display text-lg md:text-2xl text-white leading-snug">
            Tu propia lógica lo sostiene: para que una compañía funcione, los
            objetivos personales tienen que estar alineados con los colectivos.
            Tu marca personal y el objetivo de la empresa no compiten están
            diseñados para tirar en el mismo lado.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default GastonFrentes;
